import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Tables from "layouts/tables";
import { useState } from "react";
import {
  useGetAppointmentsQuery,
  useUpdateAppointmentResponseMutation,
} from "slices/wellness/concernSlice";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Typography,
} from "@mui/material";

function Consultations() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [responseMessage, setResponseMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const { data, isLoading, refetch } = useGetAppointmentsQuery({ page, limit, search });
  const [updateResponse] = useUpdateAppointmentResponseMutation();

  const appointments = data?.data || data?.appointments || data?.list || [];
  const totalPages = data?.totalPages || data?.pages || 0;

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
  };

  const changePage = (newPage, newLimit) => {
    setPage(newPage + 1);
    setLimit(newLimit);
  };

  const openModal = (appointment) => {
    setSelectedAppointment(appointment);
    setResponseMessage(appointment?.responed_message || "");
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedAppointment(null);
    setResponseMessage("");
  };

  const handleSubmit = async () => {
    if (!responseMessage.trim()) return;
    try {
      setSubmitting(true);
      await updateResponse({
        id: selectedAppointment._id,
        body: { responed_message: responseMessage },
      }).unwrap();
      refetch();
      closeModal();
    } catch (err) {
      console.error("Failed to update response:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      display: "Name",
      key: "name",
      type: "display",
      render: (row) => (
        <span className="font-medium text-gray-800">{row.name || "—"}</span>
      ),
    },
    {
      display: "Mobile",
      key: "number",
      type: "display",
      render: (row) => row.number || "—",
    },
    {
      display: "Gender",
      key: "gender",
      type: "display",
      render: (row) => (
        <span style={{ textTransform: "capitalize" }}>{row.gender || "—"}</span>
      ),
    },
    {
      display: "Concern",
      key: "concern",
      type: "display",
      render: (row) => row.concern?.title || row.concernTitle || "—",
    },
    {
      display: "Doctor",
      key: "consultation",
      type: "display",
      render: (row) => row.consultation?.name || row.doctorName || "—",
    },
    {
      display: "Slot",
      key: "slot",
      type: "display",
      render: (row) => row.slot || "—",
    },
    {
      display: "Message",
      key: "message",
      type: "display",
      render: (row) => row.message || "—",
    },
    {
      display: "Response",
      key: "responed_message",
      type: "display",
      render: (row) =>
        row.responed_message ? (
          <span style={{ color: "#22c55e", fontWeight: 600 }}>
            {row.responed_message}
          </span>
        ) : (
          <span style={{ color: "#94a3b8", fontStyle: "italic" }}>No response</span>
        ),
    },
    {
      display: "Action",
      key: "_id",
      type: "display",
      render: (row) => (
        <Button
          size="small"
          variant="contained"
          onClick={() => openModal(row)}
          style={{
            background: "#338ced",
            color: "#fff",
            textTransform: "none",
            fontSize: 12,
          }}
        >
          {row.responed_message ? "Edit Response" : "Respond"}
        </Button>
      ),
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Tables
        title="Appointments"
        handleSearch={handleSearchChange}
        totalPages={totalPages}
        data={appointments}
        columns={columns}
        changePage={changePage}
        currentPage={page}
        rowsPerPage={limit}
      />
      <Footer />

      {/* Response Modal */}
      <Dialog open={modalOpen} onClose={closeModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Add Response
          {selectedAppointment && (
            <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
              Patient: {selectedAppointment.name || "—"} &nbsp;|&nbsp;
              Concern: {selectedAppointment.concern?.title || selectedAppointment.concernTitle || "—"}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Response Message"
            multiline
            rows={4}
            fullWidth
            value={responseMessage}
            onChange={(e) => setResponseMessage(e.target.value)}
            placeholder="Enter your response here..."
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeModal} variant="outlined" color="inherit" sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting || !responseMessage.trim()}
            style={{ background: "#338ced", color: "#fff", textTransform: "none" }}
          >
            {submitting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Consultations;
